import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { LuChevronDown } from '@platform/icon';

import { useDocPrefetch, useDocsTree } from '../api/use-docs.query';
import { indexDocOf, listedDocsOf } from '../lib/docsNavigation';
import { DocsSectionIcon } from './DocsSectionIcon';

interface DocsSidebarProps {
  /** 모바일 드로어에서 항목을 눌렀을 때 닫기. */
  onNavigate?: () => void;
  /** 관리 화면에서 초안까지 보여줄 때. */
  manage?: boolean;
}

/** 스켈레톤 그룹 수 — 실제 섹션 수와 비슷해야 도착 시 높이가 덜 튄다. */
const SKELETON_GROUPS = 3;

/** 트리가 오기 전에도 자리를 잡아둔다 — 도착하는 순간 레이아웃이 튀면 안 된다. */
function SidebarSkeleton() {
  return (
    <div className="space-y-5 px-2.5 py-2" aria-hidden>
      {Array.from({ length: SKELETON_GROUPS }, (_, group) => (
        <div key={group} className="space-y-2">
          <div className="h-3.5 w-24 rounded bg-neutral-100 dark:bg-white/8" />
          <div className="h-3 w-32 rounded bg-neutral-100 dark:bg-white/8" />
          <div className="h-3 w-28 rounded bg-neutral-100 dark:bg-white/8" />
        </div>
      ))}
    </div>
  );
}

/** 지금 주소가 이 섹션 안인지. `/docs/xcost` 가 `/docs/xcost-pro` 에 걸리지 않게 경계까지 본다. */
function isInSection(pathname: string, sectionSlug: string): boolean {
  const base = `/docs/${sectionSlug}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * 문서 트리. 순서·구성은 전부 서버가 준 tree 에서 온다.
 *
 * 섹션은 기본으로 접혀 있다 — 섹션 11개에 문서가 수십 개라 다 펼치면 지금 보는
 * 문서를 찾기 어렵다. 단, **지금 보고 있는 섹션은 펼친다.** 접혀 있으면 현재 문서가
 * 사이드바에서 사라진다. 사용자가 직접 접거나 편 섹션은 그 선택을 따른다.
 */
export function DocsSidebar({ onNavigate, manage = false }: DocsSidebarProps) {
  const { data: tree, isPending } = useDocsTree(manage);
  const prefetchProps = useDocPrefetch(manage);
  const { pathname } = useLocation();
  // 사용자가 직접 접거나 편 섹션(id → 펼침 여부). 없는 섹션은 기본값을 따른다.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  if (isPending) return <SidebarSkeleton />;

  return (
    <nav aria-label="도움말 문서 목록" className="pb-8">
      {tree?.sections.map((section) => {
        // 대표 문서가 있으면 섹션 제목 자체가 그 문서 링크다. 목록에 또 넣으면
        // `FAQ` 아래 `FAQ` 가 한 번 더 붙는다.
        const landing = indexDocOf(section);
        const listed = listedDocsOf(section);
        const collapsible = listed.length > 0;
        const open =
          collapsible &&
          (toggled[section.id] ?? isInSection(pathname, section.slug));
        const listId = `docs-sidebar-${section.id}`;
        const toggle = () =>
          setToggled((prev) => ({ ...prev, [section.id]: !open }));

        const chevron = collapsible && (
          <LuChevronDown
            aria-hidden
            className={[
              'h-4 w-4 shrink-0 text-neutral-400 transition-transform',
              open ? '' : '-rotate-90',
            ].join(' ')}
          />
        );

        return (
        <div key={section.id} className="mb-1.5">
          {landing ? (
            <div className="flex items-center">
              <NavLink
                to={landing.path}
                onClick={onNavigate}
                {...prefetchProps(section.slug, landing.slug)}
                className={({ isActive }) =>
                  [
                    'flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 py-2 text-[15px] font-bold transition-colors',
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/12 dark:text-brand-300'
                      : 'text-neutral-800 hover:bg-neutral-100 dark:text-neutral-100 dark:hover:bg-white/5',
                  ].join(' ')
                }
              >
                <DocsSectionIcon section={section} className="h-4.5 w-4.5" />
                {section.title}
                {!landing.isPublished && (
                  <span className="rounded bg-warning/15 px-1.5 py-px text-[10px] font-bold text-amber-700 dark:text-amber-300">
                    초안
                  </span>
                )}
              </NavLink>
              {/* 제목은 대표 문서 링크라, 접기는 옆 버튼이 따로 맡는다. */}
              {collapsible && (
                <button
                  type="button"
                  onClick={toggle}
                  aria-expanded={open}
                  aria-controls={listId}
                  aria-label={`${section.title} ${open ? '접기' : '펼치기'}`}
                  className="rounded-lg p-2 hover:bg-neutral-100 dark:hover:bg-white/5"
                >
                  {chevron}
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={toggle}
              disabled={!collapsible}
              aria-expanded={collapsible ? open : undefined}
              aria-controls={collapsible ? listId : undefined}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[15px] font-bold text-neutral-800 enabled:hover:bg-neutral-100 dark:text-neutral-100 dark:enabled:hover:bg-white/5"
            >
              <DocsSectionIcon section={section} className="h-4.5 w-4.5" />
              <span className="flex-1">{section.title}</span>
              {chevron}
            </button>
          )}
          {open && (
          <ul id={listId}>
            {listed.map((doc) => (
              <li key={doc.id}>
                <NavLink
                  to={doc.path}
                  onClick={onNavigate}
                  // 잠깐 머무르면 본문을 당겨온다 → 클릭 즉시 렌더.
                  {...prefetchProps(section.slug, doc.slug)}
                  className={({ isActive }) =>
                    [
                      'block rounded-lg py-1.5 pr-2.5 pl-6 text-[13.5px] leading-snug transition-colors',
                      isActive
                        ? 'bg-brand-50 font-bold text-brand-700 shadow-[inset_2px_0_0_var(--color-brand-500)] dark:bg-brand-500/12 dark:text-brand-300'
                        : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-white/5',
                    ].join(' ')
                  }
                >
                  {doc.title}
                  {!doc.isPublished && (
                    <span className="ml-1.5 rounded bg-warning/15 px-1.5 py-px text-[10px] font-bold text-amber-700 dark:text-amber-300">
                      초안
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
          )}
        </div>
        );
      })}
    </nav>
  );
}
